import PropTypes from 'prop-types';
import React from 'react';

import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

import { Notification } from './Notification.js';

import axios from "axios";
import { isEqual, isEmpty } from "underscore";
import qs from "qs";

const ITEMS_PER_PAGE = 30;

const MAP_SOURCE_TO_PAGE = {
    'sensor/([^/]+)(.*)': "devices/$1",
    'connections': "settings/devices",
    'config/omshepherd': "settings/general",
    'group/([^/]+)(.*)': "dc/locate;$1",
    '(apiv2/backup/.*)': '$1'
};

const MAP_SOURCE_LINK_TO_NAME = {
    'config/.*': "Config", 
    'apiv2/backup/.*': 'Download'
};

// Instantiate all regexp only once
let MAP_REGEX_TO_PAGE = {};
let MAP_REGEX_TO_NAME = {};

for (let pattern in MAP_SOURCE_TO_PAGE) {
    MAP_REGEX_TO_PAGE[pattern] = new RegExp(pattern);
}

for (let pattern in MAP_SOURCE_LINK_TO_NAME) {
    MAP_REGEX_TO_NAME[pattern] = new RegExp(pattern);
}

const DATE_FORMAT = { year: 'numeric', month: "numeric", day: 'numeric' };
const TIME_FORMAT = { "hour": "numeric", "minute": "numeric", "second": "numeric" };

export class NotificationPage extends React.Component {

    constructor(props) {
        super(props);

        this._notifications = [];
        this._lastNotificationId = 0;
        this.filter = {
            'state': [],
            'severity': [],
            'type': null,
            'subType': []
        };

        if (props.sensorId) {
            this.filter.sensorId = props.sensorId;
        }

        if (props.location.search) {
            const query = qs.parse(props.location.search, { ignoreQueryPrefix: true });

            // Set initial severity and state filters
            if (query?.severity && query.severity.indexOf("all") === -1) {
                this.filter.severity = query.severity.split(",");
            }
            if (query?.state  && query.state.indexOf("all") === -1) {
                this.filter.state = query.state.split(",").filter(item => item != "all");
            }
            if (query?.type  && query.type.indexOf("all") === -1) {
                this.filter.type = query.type;
            }
            if (query?.subType  && query.subType.indexOf("all") === -1) {
                this.filter.subType = query.subType.split(",").filter(item => item != "all");
            }
            if (query.id) {
                this.state = Object.assign(this.state, this.loadCommentUpdates(+query.id));
            }
        }

        const pathName = this.props.location.pathname;
        const pathPrefixLen = pathName.indexOf("/notifications");
        if (pathPrefixLen > 0) {
            this.pathPrefix = pathName.substring(0, pathPrefixLen);
        }
    }

    static contextTypes = {
        t: PropTypes.func.isRequired,
        p: PropTypes.object.isRequired
    }
    
    state = {
        loading: false,
        notifications: [],
        types: [],
        subTypes: [],
        lastId: -1,
        page: 1,
        openId: -1,
        updatesLoading: false,
        confirmMarkAll: false
    };

    _isMounted = false;
    _requestId = 1;

    mapSourceToLink(source, sourceLink) {
        if (!sourceLink) {
             return null;
        }
        if (!source) {
            for (let pattern in MAP_SOURCE_LINK_TO_NAME) {
                if (sourceLink.match(MAP_REGEX_TO_NAME[pattern])) {
                    source = MAP_SOURCE_LINK_TO_NAME[pattern];
                    break;
                }
            }
            if (!source) {
                return null;
            }
        }

        const opts = {};
        if (sourceLink.indexOf("api") <= 1) {
            opts.target = "_blank";
        }
        for (let pattern in MAP_SOURCE_TO_PAGE) {
            let transformedUrl = sourceLink.replace(MAP_REGEX_TO_PAGE[pattern], MAP_SOURCE_TO_PAGE[pattern]);
            if (transformedUrl !== sourceLink) {
                return (
                    <Link {...opts} to={ "/" + transformedUrl}>{ source }</Link>
                );
            }
        }
        return (
            <Link {...opts} to={ "/" + sourceLink}>{ source }</Link>
        )
    }

    fixupNotification(not) {
        const t = this.context.t;
        if (!not.date || not.time)  {
            let dateInst = new Date(not.dateCreated);
            
            not.time = Intl.DateTimeFormat(navigator.language, TIME_FORMAT).format(dateInst);
            not.date = Intl.DateTimeFormat(navigator.language, DATE_FORMAT).format(dateInst);
        }
        not.severity = not.severity.toLowerCase();
        if (not.type) {
            not.type = not.type.toLowerCase();
        }
        if (not.subType) {
            not.subType = not.subType.toLowerCase();
        }
        not.state = not.state?.toLowerCase();

        if (not.severity === "warn") {
            not.levelClass = "level-two";
        } else if (not.severity === "error" || not.severity === "critical") {
            not.levelClass = "level-three";
        } else {
            not.levelClass = "level-one";
        }
        not.levelName = t("notifications.severity." + not.severity.toLowerCase());
    }

    getNotificationTypes = () => {
        const self = this;
        axios.get('/api/notification/count/types')
            .then((res) => {
                self.setState({
                    types: Object.keys(res.data)
                });
            });
    }

    getNotificationSubTypes = () => {
        if (!this.filter.type || this.filter.type === "all") {
            this.setState({
                subTypes: []
            });
            return;
        }
        const self = this;
        axios.get('/api/notification/count/sub_types?type=' + this.filter.type)
            .then((res) => {
                self.setState({
                    subTypes: res.data
                });
            });
    }

    retrieveNotifications = () => {
        const self = this;

        // Ensure that we never use results from an outdated request
        this._requestId++;
        const currentRequestId = this._requestId;
        this.setState((state) => {
            if (currentRequestId === self._requestId) {
                const params = {};
                if (state.lastId != -1) {
                    params.lastId = state.lastId;
                }
                for (let key in this.filter) {
                    if (this.filter[key]) {
                        params[key] = this.filter[key];
                    }
                }
                axios.get('/api/notification/get', { params })
                    .then(function (response) {
                        if (!self._isMounted || currentRequestId !== self._requestId) {
                            return;
                        }
                        self.setState((state) => {
                            const shownNotifications = [...state.notifications];
                            const types = [...state.types];
                            const newNotifications = response.data.notifications;

                            if (newNotifications && newNotifications.length) {
                                let lastId = -1;
                                for (let not of newNotifications) {
                                    let notificationAlreadyKnown = false;
                                    // De-duplicate just in case
                                    for (let existingNot of shownNotifications) {
                                        if (existingNot.id === not.id) {
                                            notificationAlreadyKnown = true;
                                            break;
                                        }
                                    }
                                    if (!notificationAlreadyKnown) {
                                        const type = not.type;
                                        self.fixupNotification(not);
                                        shownNotifications.push(not);
                                        if (type && types.indexOf(type) === -1) {
                                            types.push(type);
                                        }
                                    }
                                    lastId = not.id;
                                }
                                return {
                                    notifications: shownNotifications,
                                    count: response.data.count + ((state.page - 1) * ITEMS_PER_PAGE),
                                    lastId,
                                    types,
                                    loading: false
                                };
                            } else {
                                return {loading: false};
                            }
                        });
                    })
                    .catch(function (error) {
                        console.error(error);
                        return {loading: false};
                    });
                return { "loading": true };
            }
        });
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.context.p.removeNotificationListener(this);
    }

    newNotification = (not) => {
        this.setState((state) => {
            // If we're not on the first page, no use if adding notifications
            let pushToPage = state.page === 1;
            let addCount = true;

            if (pushToPage) {
                for (let thisNotification of state.notifications) {
                    if (not.id === thisNotification.id) {
                        // We are already showing this notification
                        pushToPage = false;
                    }
                }
            }
            // Preserve type before we lowercase it during fixup
            const type = not.type;

            this.fixupNotification(not);

            // If we are filtering, check if this notification conforms to the filter
            if (this.filter.state && this.filter.state.length && this.filter.state.indexOf(not.state) === -1) {
                addCount = pushToPage = false;
            }

            if (this.filter.severity && this.filter.severity.length && this.filter.severity.indexOf(not.severity) === -1) {
                addCount = pushToPage = false;
            }

            if (this.filter.type && this.filter.type !== "all" && type && this.filter.type !== type) {
                addCount = pushToPage = false;
            }

            if (this.filter.subType && this.filter.subType.length && this.filter.subType.indexOf(not.subType) === -1) {
                addCount = pushToPage = false;
            }

            if (this.filter.sensorId) {
                if (!not.sourceLink || not.sourceLink?.indexOf("sensor/" + this.filter.sensorId) !== 0) {
                    addCount = pushToPage = false;
                }
            }

            const update = {};

            if (addCount) {
                update.count = state.count + 1;
            }

            if (pushToPage) {
                const shownNotifications = [...state.notifications];
                shownNotifications.push(not);

                let _notificationSort = function (a, b) {
                    return b.id - a.id;
                };
                shownNotifications.sort(_notificationSort);
                update.notifications = shownNotifications;
                update.lastId = shownNotifications[shownNotifications.length - 1].id;
            }

            if (state.types.indexOf(type) === -1) {
                update.types = [...state.types];
                update.types.push(type);
            }

            return update;
        });

    }

    updatedNotification = (updatedNotRaw) => {
        const updatedNot = updatedNotRaw;
        updatedNot.severity = updatedNot.severity?.toLowerCase();
        updatedNot.type = updatedNot.type?.toLowerCase();
        updatedNot.subType = updatedNot.subType?.toLowerCase();
        updatedNot.state =  updatedNot.state?.toLowerCase();
        updatedNot.oldState =  updatedNot.oldState?.toLowerCase();
        const self = this;
        this.setState((state) => {
            let existingNot = null;
            for (let thisNotification of state.notifications) {
                if (updatedNot.id === thisNotification.id) {
                    // Update the notification
                    existingNot = thisNotification;
                    existingNot.state = updatedNot.state;
                    existingNot.userComment = updatedNot.comment;
                    existingNot.userId = updatedNot.userId;
                    break;
                }
            }

            // Check if the updated notification would have fit the current filter
            let decreaseCount = false;
            const hasStateFilter = self.filter.state?.length > 0;
            const oldState = updatedNot.oldState || 'open';
            if (!self.filter.type?.length || self.filter.type.indexOf(updatedNot.type) !== -1) {
                if (!self.filter.subType?.length || self.filter.subType.indexOf(updatedNot.subType) !== -1) {
                    if (!self.filter.sensorId || self.filter.sensorId === updatedNot.sensorId) {
                        if (!hasStateFilter) {
                            decreaseCount = true;
                        } else {
                            const wasInFilter = self.filter.state.indexOf(oldState) !== -1;
                            const isNoLongerInFilter = self.filter.state.indexOf(updatedNot.state) === -1;
                            decreaseCount = wasInFilter && isNoLongerInFilter;
                        }
                    }
                }
            }

            if (existingNot === null) {
                // We don't know this notification, do nothing
                // TODO: check filter criteria; this notification might now show in filtering
                return;
            }

            // Now take all existing notifications except for the updated one
            const shownNotifications = state.notifications.filter(n => n.id !== updatedNot.id);
            // Add the updated one
            shownNotifications.push(existingNot);

            let _notificationSort = function (a, b) {
                return b.id - a.id;
            };
            shownNotifications.sort(_notificationSort);

            // If the currently open notification has changed reload all updates to it
            let stateUpdate;
            if (state.openId === updatedNot.id) {
                stateUpdate = self.loadCommentUpdates(updatedNot.id);
            } else {
                stateUpdate = {};
            }

            if (decreaseCount) {
                stateUpdate.count = state.count - 1;
            }

            stateUpdate.notifications = shownNotifications;

            return stateUpdate;
        });
    }
    
    componentDidMount() {
        this._isMounted = true;
        const self = this, p = this.context.p;
        p.onAuthenticated(function() {
            self.retrieveNotifications();
            self.getNotificationTypes();
            self.getNotificationSubTypes();
        });

        p.addNotificationListener(this);
    }

    showNextPage = () => {
        const self = this;
        this.setState((state) => {
            if (state.loading) {
                return;
            }

            // Check if we need to load more before we can show a full page
            if ((state.page + 1) * ITEMS_PER_PAGE > state.notifications.length) {
                self.retrieveNotifications();
            }
                
            return {page: state.page + 1};
        });
    }

    showPreviousPage = () => {
        this.setState((state) => {
            if (state.page !== 1) {
                return {page: state.page - 1};
            }
        });
    }

    componentDidUpdate(prevPros, prevState) {
        const nextProps = this.props;
        let doReloadNotifications = false, doReloadSubTypes = false;
        if (nextProps.location.search) {
            const query = qs.parse(nextProps.location.search, { ignoreQueryPrefix: true });

            for (let key in query) {
                if (key === "id" || key === "tab" || key === "uuid") {
                    continue;
                }
                let items = null;
                if (query[key]) {
                    if (key === "type") {
                        // Type is not multi-selectable currently
                        items = query[key] === "all" ? null : query[key];
                    } else {
                        if (query[key].indexOf("all") !==  -1) {
                            items = [];
                        } else {
                            items = query[key].split(",");
                        }
                    }
                }

                if (!isEqual(items, this.filter[key])) {
                    this.filter[key] = items;
                    doReloadNotifications = true;
                    if (key === "type") {
                        doReloadSubTypes = true;
                    }
                }
            }
        }

        if (doReloadSubTypes) {
            this.getNotificationSubTypes();
        }

        if (doReloadNotifications) {
            this.setState({
                loading: true,
                lastId: -1,
                notifications: [],
                page: 1,
                count: 0,
                confirmMarkAll: false
            });
            this.retrieveNotifications();
        }

    }

    addFilter = (type, item) => {
        let filter = Object.assign({}, this.filter);
        if (!item) {
            // This unconditionally unsets this filter type
            filter[type] = null;
        } else {
            if (filter[type]) {
                // We already have a config for the type. See if it is already set
                if (filter[type].indexOf(item) === -1) {
                    // Not set, set it
                    filter[type] = filter[type].concat(item);
                } else {
                    // It is set, see if removing it will remove the whole filter
                    if (filter[type].length === 1) {
                        filter[type] = null;
                    } else {
                        // otherwise, unset it
                        filter[type] = filter[type].filter(thisItem => thisItem !== item);
                    }
                }
            } else {
                // No, there is nothing set for this key, overwrite it
                filter[type] = [ item ];
            }
        }
        
        if (!this.updateRouteFromFilter(filter)) {
            this.setState({
                [type]: filter[type],
                page: 1,
            });
        }
    }

    addSingleFilter = (type, item) => {
        let filter = Object.assign({}, this.filter);
        if (!item) {
            // This unconditionally unsets this filter type
            filter[type] = null;
        } else {
            filter[type] = item;
        }
        const stateUpdate = {
            [type]: filter[type]
        };

        // Also reset subtype filter if type changes
        if (type === "type") {
            filter.subType = [];
            stateUpdate.subType = [];
        }
        
        if (!this.updateRouteFromFilter(filter)) {
            this.setState(stateUpdate);
        }
    }

    updateRouteFromFilter(filter, newOpenId) {
        const query = {};
        if (newOpenId || this.state.openId) {
            query.id = "" + (newOpenId ? newOpenId : this.state.openId);
        }
        
        query.severity = (filter.severity ? filter.severity.join(",") : null) || "all";
        query.state = (filter.state ? filter.state.join(",") : null) || "all";
        query.type = (filter.type ? filter.type : null) || "all";
        query.subType = (filter.subType ? filter.subType.join(",") : null) || "all";

        const searchParam = query ? ("?" + qs.stringify(query)) : "";

        const newRoute = (this.pathPrefix ? this.pathPrefix : "") + "/notifications";

        if (this.props.location.pathname !== newRoute || searchParam !== this.props.location.search) {
            this.props.history.replace({
                pathname: newRoute,
                search: searchParam
            });
            return true;
        }
    }

    checkedForFilterButton(key, value) {
        const filter = this.filter[key];
        if (!value) {
            return isEmpty(filter) || !filter;
        }
        if (!filter) {
            return false;
        }
        return filter.indexOf(value) !== -1;
    }

    checkedForFilterDropDown(key, value) {
        const filter = this.filter[key];
        if (!value) {
            return !filter;
        }
        if (!filter) {
            return false;
        }
        return filter !== value;
    }

    renderFilterCheckbox(filterKey, filterVal, otherTranslationSpaces) {
        let title;
        if (!filterVal) {
            title = this.context.t("notifications.title_show_all");
        } else if (otherTranslationSpaces) {
            title = this.context.t(["notifications." + filterKey, ...otherTranslationSpaces].map((space) => space + "." + filterVal.toLowerCase()));
        } else {
            title = this.context.t("notifications." + filterKey + "." + filterVal.toLowerCase());
        }

        const checked = this.checkedForFilterButton(filterKey, filterVal);

        // The "all" checkbox shouldn't be clickable if already checked
        let checkedCallback = (checked && !filterVal) ? () => { } : () => {
            this.addFilter(filterKey, filterVal);
        };
        // Also show via pointer
        const classNames = "form-icon" + ((checked && !filterVal) ? " not-clickable" : "");

        return (
            <div className="form-group" key={filterKey + "_" + filterVal}>
                <label className="form-checkbox">
                    <input type="checkbox" checked={checked} onChange={checkedCallback} />
                    <i className={classNames}></i> {title}
                </label>
            </div>
        );
    }

    renderFilterDropdown(filterKey, filterVal, otherTranslationSpaces) {
        let title;
        if (!filterVal) {
            title = this.context.t("notifications.title_show_all");
        } else {
            if (otherTranslationSpaces) {
                title = this.context.t(
                    ["notifications." + filterKey, ...otherTranslationSpaces].map((space) => space + "." + filterVal.toLowerCase()));
            } else {
                title = this.context.t("notifications." + filterKey + "." + filterVal.toLowerCase());
            }
        }

        return (
            <option key={filterKey + "_" + filterVal} value={filterVal || ""}>{title}</option>
        );
    }

    openNotification(id) {
        const self = this;
        this.setState((state) => {
            if (state.openId != id) {
                return self.loadCommentUpdates(id);
            }
        });
    }

    loadCommentUpdates(id) {
        const self = this;
        axios.get('/api/notification/updates/' + id)
            .then((res) => {
                self.setState((state) =>  {
                    if (state.openId === id) {
                        return {
                            updatesLoading: false,
                            updates: res.data
                        }
                    }
                });
            });

        this.updateRouteFromFilter(this.filter, id);
        return {
            openId: id,
            updatesLoading: true,
            updates: []
        };
    }

    setComment = (ev) => {
        this.setState({ comment: ev.target.value });
    }

    sendNewState = (newState) => {
        const self = this;
        this.setState((state) => {
            if (state.loading) {
                return;
            }

            const id = state.openId;

            axios.post('/api/notification/update', {
                id: state.openId,
                state: newState.toUpperCase()
            }).then(() => {
                self.setState((state) => {
                    if (state.openId != id) {
                        return;
                    }
                    return Object.assign(self.loadCommentUpdates(this.state.openId), { loading: false });
                });
            }, (err) => {
                console.error('Cannot update notifications', err);
                self.setState({
                    loading: false,
                    error: true
                });
            });
            return {
                loading: true
            };
        });
    }

    submitComment = () => {
        const self = this;
        this.setState((state) => {
            if (state.loading) {
                return;
            }

            axios.post('/api/notification/update', {
                id: state.openId,
                comment: state.comment
            }).then(() => {
                self.setState({
                    loading: false,
                    comment: ""
                });
            }, (err) => {
                console.error('Cannot update notification', err);
                self.setState({
                    loading: false,
                    error: true
                });
            });
            return {
                loading: true
            };
        });
    }

    markAllDone = () => {
        const self = this;
        this.setState((state) => {
            if (state.confirmMarkAll) {
                // Send request
                const params = {};
                for (let key in this.filter) {
                    if (this.filter[key]) {
                        if (typeof this.filter[key] !== 'string') {
                            params[key] = this.filter[key].map(val => val.toUpperCase());
                        } else {
                            params[key] = this.filter[key];
                        }
                    }
                }
                self.context.p.removeNotificationListener(this);
                axios.post('/apiv2/notification/updateAll', {
                    ...params
                })
                    .then(function () {
                        if (!self._isMounted) {
                            return;
                        }

                        self.context.p.addNotificationListener(self);
                        self.setState({
                            loading: true,
                            lastId: -1,
                            notifications: [],
                            count: 0
                        });
                        self.retrieveNotifications();
                    })
                    .catch(err => {
                        console.error('Cannot update all notifications', err);
                        self.setState({
                            loading: false
                        });
                    });

                return {
                    confirmMarkAll: false,
                    loading: true
                };
            } else {
                return {
                    confirmMarkAll: true
                };
            }
        });
    }

    render() {
        const self = this;
        const sliceStart = (this.state.page - 1) * ITEMS_PER_PAGE, t = this.context.t;
        let rows = [], sortedNotifications = this.state.notifications.slice(sliceStart, sliceStart + ITEMS_PER_PAGE - 1);
        // let _notificationSort = function (a, b) {
        //     return b.dateCreated - a.dateCreated;
        // };
        // sortedNotifications.sort(_notificationSort);

        let openNotification = null;
        for (let i in sortedNotifications) {
            let notification = sortedNotifications[i];
            let onClickCb = () => {
                this.openNotification(notification.id);
            };
            let row = (
                <Notification {...notification} key={"not-" + notification.id} onNotificationSelected={onClickCb}
                              isActive={this.state.openId === notification.id}/>
            );
            rows.push(row);

            if (notification.id === this.state.openId) {
                const notificationState = notification.state ? notification.state.toLowerCase() : "open";
                const isResolvable = notification.severity !== "INFO";

                let toggleButtons = [];

                if (isResolvable) {
                    for (let state of ["open", "investigating", "done"]) {
                        if (state === notificationState) {
                            toggleButtons.push((<span key={"state-" + state}
                                                      className="active">{t("notifications.state." + state)}</span>));
                        } else {
                            let updateState = () => this.sendNewState(state);
                            toggleButtons.push((<span key={"state-" + state}
                                                      onClick={updateState}>{t("notifications.state." + state)}</span>));
                        }
                    }
                }

                let updateNodes = [];
                for (const update of this.state.updates) {
                    const updateInst = new Date(update.created);

                    const time = Intl.DateTimeFormat(navigator.language, TIME_FORMAT).format(updateInst);
                    const date = Intl.DateTimeFormat(navigator.language, DATE_FORMAT).format(updateInst);
                    let comment;
                    if (update.comment) {
                        comment = update.comment;
                    } else {
                        comment = t("notifications.state_changed_to__", {state: t("notifications.state." + update.state.toLowerCase())});
                    }
                    updateNodes.push((
                        <React.Fragment>
                            <p>{comment}</p>
                            <div className="author">
                                {update.userId} · {time} · {date}
                            </div>
                        </React.Fragment>
                    ));
                }
                openNotification = (
                    <div className="column col-5 detail">
                        <div className="wrapper">
                            <div className="item">
                                <div className="meta">
                                    {t("notifications.title_source")}: <strong>
                                    {this.mapSourceToLink(notification.source, notification.sourceLink)}
                                </strong><br/>
                                    {t("notifications.title_time")}: <strong>{notification.time}</strong><br/>
                                    {t("notifications.title_date")}: <strong>{notification.date}</strong><br/>
                                    {t("notifications.title_severity")}: <span
                                    className={notification.severity.toLowerCase()}>{t("notifications.severity." + notification.severity.toLowerCase())}</span>
                                </div>
                                <ReactMarkdown linkTarget={() => "_blank"}>{notification.message}</ReactMarkdown>
                                <div className="options">
                                    {toggleButtons}
                                </div>
                                <div className="comments">
                                    {updateNodes}

                                    <div className="newcomment">
                                        <textarea placeholder={t("notifications.hint_new_comment")}
                                                  value={this.state.comment} onChange={this.setComment}></textarea>
                                        <button className="btn"
                                                onClick={this.submitComment}>{t("notifications.button_submit_comment")}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }
        }

        const maxPage = this.state.count ? Math.max(1, Math.floor(this.state.count / ITEMS_PER_PAGE)) : 1;

        let typeChangeCb = (ev) => {
            ev.preventDefault();
            self.addSingleFilter("type", ev.target.value);
        }

        const typeItems = [];
        for (const type of this.state.types) {
            typeItems.push(this.renderFilterDropdown("type", type, ["device_types", "connectionState"]));
        }

        const subTypeItems = [];

        if (this.state.subTypes) {
            subTypeItems.push(
                <h4 key="subTypeHeader">{t("notifications.title_filter_by_sub_type")}</h4>
            );
            subTypeItems.push(this.renderFilterCheckbox("subType"));
            for (const subType in this.state.subTypes) {
                subTypeItems.push(this.renderFilterCheckbox("subType", subType, ["topic_titles", "notifications.sub_type", "connectionState", "device_types"]));
            }
        }

        const confirmButtonClass = this.state.loading ? 'loading' : this.state.confirmMarkAll ? "btn-error" : "btn-primary";

        return (
            <>
                { this.state.loading && (
                    <div className="notificationLoadingParent">
                        <div key="loader" className="loader-element">
                            <div className="loading loading-lg"></div>
                        </div>
                    </div>
                )}
                <div className="columns notifications">
                    <div className="column col-3 filters">
                        <div className="wrapper">
                            <h4>{t("notifications.title_filter_by_severity")}</h4>
                            { this.renderFilterCheckbox("severity") }
                            { this.renderFilterCheckbox("severity", "info") }
                            { this.renderFilterCheckbox("severity", "warn") }
                            { this.renderFilterCheckbox("severity", "error") }
                            { this.renderFilterCheckbox("severity", "critical") }
                            <br />
                            <h4>{t("notifications.title_filter_by_state")}</h4>
                            { this.renderFilterCheckbox("state") }
                            { this.renderFilterCheckbox("state", "open") }
                            { this.renderFilterCheckbox("state", "investigating") }
                            { this.renderFilterCheckbox("state", "done") }
                            <br />
                            <h4>{t("notifications.title_filter_by_type")}</h4>
                            <div className="form-group">
                                <select className="form-select" onChange={typeChangeCb} value={this.filter.type || ""}>
                                    { this.renderFilterDropdown("type") }
                                    { typeItems }
                                </select>
                            </div>

                            <br />
                            <h4>{t("notifications.title_mass_actions")}</h4>
                            <div className="form-group">
                                <button disabled={ this.state.count <= 0 || this.state.loading } className={`btn ${confirmButtonClass}` } onClick={this.markAllDone}>
                                    { this.state.count ? 
                                        t(this.state.confirmMarkAll ? "general.click_to_confirm" : "notifications.button_mark_all__done", {count: this.state.count}) :
                                        t("notifications.error_filter_empty")
                                    }
                                </button>
                            </div>

                            { subTypeItems }

                        </div>
                    </div>

                    <div className="column col-4 list">
  		                <div className="wrapper">
                            { rows && rows.length > 2 && (
                                <div className="notificationPagination">
                                    <button disabled={this.state.page <= 1} className="btn btn-primary" onClick={this.showPreviousPage}>{t("general.previous_page")}</button>
                                    <span className="pageText">
                                        { t("general.page__of__", {page: this.state.page, max: maxPage}) }
                                    </span>
                                    <button disabled={ this.state.page >= maxPage } className="btn btn-primary" onClick={this.showNextPage}>{t("general.next_page")}</button>
                                </div>
                            )}
                            { rows }
                            <div className="notificationPagination">
                                <button disabled={this.state.page <= 1} className="btn btn-primary" onClick={this.showPreviousPage}>{t("general.previous_page")}</button>
                                <span className="pageText">
                                    { t("general.page__of__", {page: this.state.page, max: maxPage}) }
                                </span>
                                <button disabled={ this.state.page >= maxPage } className="btn btn-primary" onClick={this.showNextPage}>{t("general.next_page")}</button>
                            </div>
                        </div>
                    </div>
                    { openNotification }
                </div>
            </>
        );


                    /* 
                    <div className="column col-4 col-lg-6 col-md-12">
                    <form className="form-horizontal">
                        <div className="form-group">
                            <div className="col-3 col-sm-12">
                                <label className="form-label" htmlFor="category">Category: </label>
                            </div>
                            <div className="col-9 col-sm-12">        
                                <select className="form-select" name="category">
                                    <option>Choose an option</option>
                                    <option>Slack</option>
                                    <option>Skype</option>
                                    <option>Hipchat</option>
                                </select>
                            </div>
                        </div>
                    </form>
                </div> 
                                <div className="column col-12 details focus">
                    
                </div>
                <div className="notificationPagination">
                    { this.state.page > 1 && (
                        <button className="btn btn-primary" onClick={this.showPreviousPage}>{t("general.previous_page")}</button>
                    )}
                    <span className="pageText">
                        { t("general.page__", {page: this.state.page}) }
                    </span>
                    <button className="btn btn-primary" onClick={this.showNextPage}>{t("general.next_page")}</button>
                </div>
            </div>
                
                */
    }
}
